import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { wrapError } from "utils/errorHelper";
import { Modal, ListGroup } from "react-bootstrap";
import { simulateLinkClickNewTab } from "utils/dom";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { getUrlForDocument } from "services/documentService";
import { auth0AccountSessionSelector } from "store/selectors/auth0";
import { FiDownload } from "react-icons/fi";
import { getRolloverInfo } from "actions/userActions";
import { userConstants } from "actions/types";
import { some, isEmpty, find, get, size } from "lodash";
import { activeRolloverIsMixed } from "store/selectors/user";
import { formatCurrency } from "utils/number";
import { accountTypesToEnglishMapping } from "statics/accountTypes";
import type { RootState } from "types/state";

import Button from "components/Button";
import IconHeader from "components/IconHeader";
import IconSubheader from "components/IconSubheader";
import PromisePoller from "components/PromisePoller";

import "./RolloverInstructionsModal.scss";
import "./IndividualOverview.scss";

const actions = [userConstants.GET_ROLLOVER_INFO];

const errorSelector = createErrorSelector(actions);
const loadingSelector = createLoadingSelector(actions);

const mapStateToProps = (state: RootState) => {
  const accounts = state.accounts.iraAccounts;
  const isMultiAccount = size(accounts) >= 2;
  // store/selectors/auth0 is TS; activeAccountId is the session account id
  const activeAccountId = auth0AccountSessionSelector(state);
  const account = find(accounts, { isProcessingRollover: true });

  const id = account ? account.id : null;
  const accountId = isEmpty(activeAccountId) ? id : activeAccountId;

  //Getting values for a single account
  const rolloverAccountById = find(accounts, {
    id: accountId,
  });
  const accountNumber = get(rolloverAccountById, "accountNumber", null);
  const accountType = get(rolloverAccountById, "accountType", null);

  // Getting Mixed accounts and values
  const traditionalRollover = find(accounts, {
    isProcessingRollover: true,
    accountType: "TRADITIONAL_IRA",
    state: "AccountActive",
  });
  const rothRollover = find(accounts, {
    isProcessingRollover: true,
    accountType: "ROTH_IRA",
    state: "AccountActive",
  });
  const rothId = get(rothRollover, "id", null);
  const rothAccountNumber = get(rothRollover, "accountNumber", null);
  const traditionalId = get(traditionalRollover, "id", null);
  const traditionalAccountNumber = get(
    traditionalRollover,
    "accountNumber",
    null
  );

  //Check to make sure if is mixed rollover has both accounts active || if single account display it
  // store/selectors/user is TS
  const isRolloverIsMixed = activeRolloverIsMixed(state);
  const mixedRolloverIsProcessing =
    !isEmpty(rothRollover) && !isEmpty(traditionalRollover);

  const accountByIdIsProcessing = some(accounts, {
    isProcessingRollover: true,
    state: "AccountActive",
  });
  const isProcessingRollover = isRolloverIsMixed
    ? mixedRolloverIsProcessing
    : accountByIdIsProcessing;

  return {
    accounts,
    isMultiAccount,
    accountId,
    accountNumber,
    accountType,
    traditionalId,
    rothId,
    rothAccountNumber,
    traditionalAccountNumber,
    isProcessingRollover,
    error: errorSelector(state),
    loading: loadingSelector(state),
    isRolloverIsMixed,
  };
};

const mapDispatchToProps = {
  getRolloverInfo,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  onClose?: () => void;
  onSuccess?: () => void;
  show?: boolean;
  hasCheckFundsTransfer?: boolean;
  auth0AccountSessionId?: string;
  rolloverInfo?: object;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  rolloverInfo: object | null;
  loading: boolean;
  error: string | null;
  documentId: string;
  accountId: string;
  amount: string;
  rothDocId: string;
  rothAccountId: string;
  rothAmount: string;
  traditionalDocumentId: string;
  traditionalAccountId: string;
  traditionalAmount: string;
}

class RolloverInstructionsModal extends React.PureComponent<Props, State> {
  _promises: Promise<unknown>[] = [];

  constructor(props: Props) {
    super(props);

    this.state = {
      rolloverInfo: null,
      loading: true,
      error: null,
      documentId: "",
      accountId: "",
      amount: "",
      rothDocId: "",
      rothAccountId: "",
      rothAmount: "",
      traditionalDocumentId: "",
      traditionalAccountId: "",
      traditionalAmount: "",
    };
  }

  componentDidMount() {
    if (!this.props.hasCheckFundsTransfer) {
      void this.verify();
    }
  }

  verify = () => {
    const {
      isRolloverIsMixed,
      client,
      traditionalId,
      rothId,
      accountId,
      getRolloverInfo,
    } = this.props;

    if (isRolloverIsMixed) {
      return Promise.all([
        getRolloverInfo(client as ApolloClient<object>, traditionalId as string)
          .then((response) => {
            const data = (response as { data: unknown }).data;
            const {
              docId: traditionalDocumentId,
              accountId: traditionalAccountId,
              amount: traditionalAmount,
            } = data as {
              docId: string;
              accountId: string;
              amount: string;
            };
            this.setState({
              traditionalDocumentId,
              traditionalAccountId,
              traditionalAmount,
            });
          })
          .catch((error) => {
            console.error("Error fetching rollover info:", error);
          }),
        getRolloverInfo(client as ApolloClient<object>, rothId as string)
          .then((response) => {
            const data = (response as { data: unknown }).data;
            const {
              docId: rothDocId,
              accountId: rothAccountId,
              amount: rothAmount,
            } = data as { docId: string; accountId: string; amount: string };
            this.setState({ rothDocId, rothAccountId, rothAmount });
          })
          .catch((error) => {
            console.error("Error fetching rollover info:", error);
          }),
      ]);
    } else {
      return getRolloverInfo(
        client as ApolloClient<object>,
        accountId as string
      )
        .then((response) => {
          const data = (response as { data: unknown }).data;
          const {
            docId: documentId,
            accountId,
            amount,
          } = data as { docId: string; accountId: string; amount: string };
          this.setState({ documentId, accountId, amount });
        })
        .catch((error) => {
          console.error("Error fetching rollover info:", error);
        });
    }
  };

  _downloadDoc() {
    const {
      traditionalDocumentId,
      traditionalAccountId,
      traditionalAmount,
      rothDocId,
      rothAccountId,
      rothAmount,
      documentId,
      accountId,
      amount,
    } = this.state;

    const {
      traditionalAccountNumber,
      rothAccountNumber,
      accountNumber,
      accountType,
    } = this.props;

    const mixedRolloverAccounts = [
      {
        index: 1,
        accountId: traditionalAccountId,
        accountType: "Traditional Rollover Form ",
        accountNumber: traditionalAccountNumber,
        amount: traditionalAmount,
        documentId: traditionalDocumentId,
      },
      {
        index: 2,
        accountId: rothAccountId,
        accountType: "Roth Rollover Form",
        accountNumber: rothAccountNumber,
        amount: rothAmount,
        documentId: rothDocId,
      },
    ];

    const rolloverAccount = [
      {
        index: 1,
        accountId,
        accountType,
        accountNumber,
        amount,
        documentId,
      },
    ];

    const activeRolloverAccounts = this.props.isRolloverIsMixed
      ? mixedRolloverAccounts
      : rolloverAccount;

    return (
      <>
        {activeRolloverAccounts.map((account) => (
          <div
            // PRESERVED BUG (conversion-only): account objects expose `index`,
            // not `key`, so this key is always undefined — kept verbatim.
            // @ts-expect-error account has no `key` field (preserved bug)
            key={account.key}
            className="rollover-link"
            onClick={() =>
              getUrlForDocument(
                this.props.client as ApolloClient<object>,
                account.accountId as string,
                account.documentId
              )
                .then((response) => {
                  // PRESERVED BUG (conversion-only): getUrlForDocument may
                  // resolve null; original read `.url` directly, so keep the
                  // throw-on-null behavior with `!`.
                  simulateLinkClickNewTab(response!.url);
                })
                .catch((error) => {
                  this.setState({ error: wrapError(error) });
                })
            }
          >
            <div className="rollover-item">
              <div className="rollover-details">
                <div className="detail-col">
                  <p className="detail">
                    <span style={{ paddingRight: 10 }}>
                      <FiDownload color="white" stroke="#60A4BF" size={22} />
                    </span>{" "}
                    {this.props.isRolloverIsMixed
                      ? account.accountType
                      : accountTypesToEnglishMapping[
                          account.accountType as keyof typeof accountTypesToEnglishMapping
                        ]}
                  </p>
                  <div className="detail">{formatCurrency(account.amount)}</div>
                </div>

                <p className="rollover-account-number">
                  Account No: {account.accountNumber}
                </p>
              </div>
            </div>
          </div>
        ))}
      </>
    );
  }

  _buildRolloverSteps = () => {
    const { isRolloverIsMixed } = this.props;

    const isMixedRolloverDocID =
      !isEmpty(this.state.rothDocId) &&
      !isEmpty(this.state.traditionalDocumentId);

    const showDocument = isRolloverIsMixed
      ? isMixedRolloverDocID
      : !isEmpty(this.state.documentId);

    return (
      <ListGroup as="ol" numbered className="custom-list-group">
        <ListGroup.Item as="li">
          1. Make check payable to:
          <p className="detail">
            &quot;Apex Clearing Corporation, FBO [Your Name]&quot;
          </p>
        </ListGroup.Item>
        <ListGroup.Item as="li">
          2. Please download and sign your rollover form in black ink at the
          bottom. Write your IRA account number on your check, then mail both
          the signed form and check together to complete your rollover.
          <br />
          {showDocument && this.props.isProcessingRollover ? (
            <>{this._downloadDoc()}</>
          ) : (
            <>
              <PromisePoller
                interval={4000}
                minutesToPollFor={10}
                promiseToPoll={this.verify}
              />
              <p className="doc-text">
                Your document is being generated. You will receive an email
                notification when it is ready!
              </p>
            </>
          )}
          <div
            style={{
              padding: 20,
              marginBottom: 20,
              background: "#F7FEFF",
              borderRadius: 5,
            }}
          >
            Some plan administrators will only mail the check to your address.
            If your account information is not on the check, please hand-write
            it on the check and forward it to the Apex Clearing Corporation, FBO
            Your Name address.
          </div>
        </ListGroup.Item>
        <ListGroup.Item as="li">
          3. Send the check to the address below:
          <div style={{ marginLeft: 15, paddingTop: 5 }}>
            <address>
              Attn: Banking Department
              <br />
              Apex Fintech Solutions
              <br />
              350 North St. Paul Street, STE 1300
              <br />
              Dallas, Texas 75201
            </address>
          </div>
        </ListGroup.Item>
      </ListGroup>
    );
  };

  render() {
    const steps = this._buildRolloverSteps();

    if (this.props.show) {
      return (
        <Modal
          // @ts-expect-error PRESERVED BUG (conversion-only): "md" is not a valid react-bootstrap Modal size ("sm" | "lg" | "xl"); inert at runtime, kept verbatim
          size="md"
          centered
          scrollable
          show={this.props.show}
          className="rollover-modal"
        >
          <Modal.Header className="modal-header">
            <IconHeader
              variant="cardHeader"
              headerText="Complete your rollover"
            />
            <IconSubheader subheader="To finish rolling over your account, contact your plan provider and request a rollover. Try calling the phone number on your statement and provide them the instructions below." />
          </Modal.Header>
          <Modal.Body>{steps}</Modal.Body>
          <Modal.Footer>
            <Button
              name="cancel"
              btnLabel="Done"
              onClick={() => this.props.onClose!()}
            />
          </Modal.Footer>
        </Modal>
      );
    }
    return null;
  }
}

export default connector(
  withApollo<OwnProps & PropsFromRedux>(RolloverInstructionsModal)
);
